/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useContext } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Container, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import { withStyles } from '@mui/styles';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { COLORS } from '../../utils/Application_Constants';
import useQuery from '../../utils/useQuery';
import { config } from '../Components/Expense/ExpenseReviewConfig';
import ExpenseFiltersForm from '../Components/Expense/ExpenseFiltersForm';
import {
    getExpensesWithFilter,
    getExpensesCSVWithFilter,
    getExpenseCategories,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import ExpenseTable from '../Components/Expense/ExpenseTable';
import Alert from '../Components/Alert';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import getCriteriaFromLocalStorage from '../Components/Expense/GetCriteriaFromLocalStorage';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { checkFeatureFlag } from '../../components/GlobalComponents/FeatureFlagComponent/FeatureFlag';
import { ExpenseClassEnum, ExpenseClassIdEnum } from '../Components/Expense/ExpenseClass.enum';

const styles = (theme) => ({
    PaperRoot: {
        padding: '1.5rem 2rem',
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
        marginTop: '2em',
    },
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '2.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    header: {
        fontWeight: '700',
        margin: 0,
    },
    paginationNumber: {
        color: COLORS.LT_MIDNIGHT50,
        fontWeight: '600',
        fontSize: '14px',
        [theme.breakpoints.down('md')]: {
            marginBottom: '15px',
        },
    },
    paginationSpace: {
        paddingTop: '2em',
    },
    outlinedPageButton: {
        color: COLORS.LT_MIDNIGHT75,
        background: 'white',
    },
});

const ExpenseReview = (props) => {
    const { classes } = props;
    const {
        ltoeWebExpensesEnableClinicianReimbursementListViewChanges: enableNewCRListViewChanges,
    } = useFlags();
    const history = useHistory();
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser, isClinicianReimEntryUser } =
        useContext(AdminAuthContext);
    const isViewOnly = !(
        isExpenseSystemAdministratorUser ||
        isExpenseAdminUser ||
        isClinicianReimEntryUser
    );
    const canDownload = !isClinicianReimEntryUser;
    const [expenses, setExpenses] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const query = useQuery();
    const [criteria, setCriteria] = useState(
        getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement)
    );
    const [page, setPage] = useState(
        query.get('page')
            ? Number(query.get('page'))
            : sessionStorage.getItem('expensePage')
            ? Number(sessionStorage.getItem('expensePage'))
            : 1
    );
    const [pages, setPages] = useState(0);
    const [sortBy, setSortBy] = useState(criteria ? criteria.sortBy : 'division');
    const [sortDirection, setSortDirection] = useState(criteria ? criteria.sortDirection : 'asc');
    const [limit, setLimit] = useState(50);
    const [alert, setAlert] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const getOffset = page === 1 ? 0 : (page - 1) * limit;
    const [offset, setOffset] = useState(getOffset);
    const [isSorting, setIsSorting] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [isNCREFFEnabled, setIsNCREFFEnabled] = useState(false); // Non-Clinician Reimbursement Expense Feature Flag check
    const defaultFormValues = {
        Statuses: criteria ? criteria.statuses : [],
        Divisions: criteria ? criteria.divisions : [],
        'Special Processing': criteria ? criteria.specialProcessing : [],
        Flags: criteria && criteria.flags ? criteria.flags : [],
        'Expense Types': criteria && criteria.expenseTypes ? criteria.expenseTypes : [],
        'Bill Types': criteria && criteria.billTypes ? criteria.billTypes : [],
        Sources: criteria && criteria.sources ? criteria.sources : [],
        'Needs Review Reasons':
            criteria && criteria.needsReviewReasons ? criteria.needsReviewReasons : [],
        'Merchant States': criteria && criteria.merchantStates ? criteria.merchantStates : [],
        StartStartDate: criteria && criteria.startStartDate ? criteria.startStartDate : null,
        StartEndDate: criteria && criteria.startEndDate ? criteria.startEndDate : null,
        EndStartDate: criteria && criteria.endStartDate ? criteria.endStartDate : null,
        EndEndDate: criteria && criteria.endEndDate ? criteria.endEndDate : null,
        PostStartDate: criteria && criteria.postStartDate ? criteria.postStartDate : null,
        PostEndDate: criteria && criteria.postEndDate ? criteria.postEndDate : null,
        PeriodEndStart: criteria && criteria.periodEndStart ? criteria.periodEndStart : null,
        PeriodEndEnd: criteria && criteria.periodEndEnd ? criteria.periodEndEnd : null,
        PayDateStart: criteria && criteria.payDateStart ? criteria.payDateStart : null,
        PayDateEnd: criteria && criteria.payDateEnd ? criteria.payDateEnd : null,
        TransactionStartDate:
            criteria && criteria.transactionStartDate ? criteria.transactionStartDate : null,
        TransactionEndDate:
            criteria && criteria.transactionEndDate ? criteria.transactionEndDate : null,
        SubmittedStartDate:
            criteria && criteria.submittedStartDate ? criteria.submittedStartDate : null,
        SubmittedEndDate: criteria && criteria.submittedEndDate ? criteria.submittedEndDate : null,
    };
    const methods = useForm({ defaultValues: defaultFormValues });
    const { handleSubmit, reset, getValues, setValue, watch } = methods;
    const watchForValues = watch();
    const [totalNumberExpense, setTotalNumberExpense] = useState(getValues('totalNumberExpense'));

    useEffect(() => {
        history.replace(`/expenses/review?page=${page}`);
        sessionStorage.setItem('expensePage', Number(page));
        getData();
    }, []);

    useEffect(() => {
        history.replace(`/expenses/review?page=${page}`);
        sessionStorage.setItem('expensePage', Number(page));
    }, [page]);

    const getNewSortValues = (sortKey) => {
        let newSortBy = sortBy;
        let newSortDirection = sortDirection;
        if (sortKey !== sortBy) {
            newSortBy = sortKey;
            newSortDirection = 'asc';
            setSortBy(sortKey);
            setSortDirection(newSortDirection);
        } else {
            newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            newSortBy = newSortDirection === 'asc' && sortDirection === 'desc' ? null : sortKey;
            if (newSortBy !== sortKey) setSortBy(newSortBy);
            setSortDirection(newSortDirection);
        }

        return { newSortBy, newSortDirection };
    };

    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const filterValues = getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement);
            const allReimbursement = ['Reimbursement (Associate)', 'Reimbursement (Clinician)'];
            const allCC = ['Corpay', 'Conferma', 'Travel Inc'];
            const ReimbursementExpenseFile =
                !filterValues.sources ||
                filterValues.sources.length === 0 ||
                filterValues.sources.some((source) => allReimbursement.includes(source));
            const request = {
                offset: 0,
                sortBy,
                sortDirection,
                limit: 150000,
                statuses: filterValues ? filterValues.statuses : [],
                divisions: filterValues ? filterValues.divisions : [],
                transactionStartDate:
                    filterValues && filterValues.transactionStartDate
                        ? filterValues.transactionStartDate
                        : null,
                transactionEndDate:
                    filterValues && filterValues.transactionEndDate
                        ? filterValues.transactionEndDate
                        : null,
                expenseTypes:
                    filterValues && filterValues.expenseTypes ? filterValues.expenseTypes : [],
                sources: filterValues && filterValues.sources ? filterValues.sources : [],
                needsReviewReasons:
                    filterValues && filterValues.needsReviewReasons
                        ? filterValues.needsReviewReasons
                        : [],
                billTypes: filterValues && filterValues.billTypes ? filterValues.billTypes : [],
                startStartDate:
                    filterValues && filterValues.startStartDate
                        ? filterValues.startStartDate
                        : null,
                startEndDate:
                    filterValues && filterValues.startEndDate ? filterValues.startEndDate : null,
                endStartDate:
                    filterValues && filterValues.endStartDate ? filterValues.endStartDate : null,
                endEndDate:
                    filterValues && filterValues.endEndDate ? filterValues.endEndDate : null,
                postStartDate:
                    filterValues && filterValues.postStartDate ? filterValues.postStartDate : null,
                postEndDate:
                    filterValues && filterValues.postEndDate ? filterValues.postEndDate : null,
                periodEndStart:
                    filterValues && filterValues.periodEndStart
                        ? filterValues.periodEndStart
                        : null,
                periodEndEnd:
                    filterValues && filterValues.periodEndEnd ? filterValues.periodEndEnd : null,
                payDateStart:
                    filterValues && filterValues.payDateStart ? filterValues.payDateStart : null,
                payDateEnd:
                    filterValues && filterValues.payDateEnd ? filterValues.payDateEnd : null,
                submittedStartDate:
                    filterValues && filterValues.submittedStartDate
                        ? filterValues.submittedStartDate
                        : null,
                submittedEndDate:
                    filterValues && filterValues.submittedEndDate
                        ? filterValues.submittedEndDate
                        : null,
                merchantStates:
                    filterValues && filterValues.merchantStates ? filterValues.merchantStates : [],
                flags: filterValues && filterValues.flags ? filterValues.flags : [],
                search: filterValues.search,
                expenseClass: ExpenseClassIdEnum.Reimbursement,
                searchKey:
                    filterValues && filterValues.searchKey
                        ? filterValues.searchKey
                        : config.searchKeys[0],
            };

            const downloadTasks = [];

            if (ReimbursementExpenseFile) {
                const reimbursementRequest = {
                    ...request,
                    sources:
                        !filterValues.sources || filterValues.sources.length === 0
                            ? allReimbursement
                            : filterValues.sources.filter((source) =>
                                  allReimbursement.includes(source)
                              ),
                };
                downloadTasks.push(
                    fetchAndDownloadCSV(reimbursementRequest, 'Reimbursement_Expenses')
                );
            }

            // Execute both requests concurrently
            await Promise.allSettled(downloadTasks);
        } catch (error) {
            console.error('Error downloading file:', error);
        } finally {
            setDownloading(false);
        }
    };

    const fetchAndDownloadCSV = async (request, filePrefix) => {
        try {
            if (totalNumberExpense > 150000) {
                throw new Error('Filtered results must be 150,000 expenses or less.');
            }
            const response = await getExpensesCSVWithFilter(request);
            if (response.status === 404)
                throw new Error(`No data found to download ${filePrefix} file`);
            if (response.status !== 200) throw new Error('Failed to download file');

            // Format timestamp and create filename
            const timestamp = moment().format('YYYYMMDD_HHmm');
            const fileName = `${filePrefix}_${timestamp}.csv`;

            // Convert response data to CSV and trigger download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`Error downloading ${filePrefix} file:`, error);
            setAlert({
                message: formatErrorMessage(error),
                type: 'error',
            });
        }
    };

    const handleSort = async (sortKey) => {
        setIsSorting(true);
        setIsLoading(true);
        const { newSortBy, newSortDirection } = getNewSortValues(sortKey, sortBy, sortDirection);

        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement);

        currentCriteria.sortBy = newSortBy;
        currentCriteria.sortDirection = newSortDirection;
        setCriteria(currentCriteria);
        await fetchData(currentCriteria);
    };

    const fetchData = async (request) => {
        localStorage.setItem('expenseCriteria', JSON.stringify(request));
        try {
            let response = await getExpensesWithFilter(request);
            setTotalNumberExpense(response.totalCount);
            setExpenses(response.data);
            let aLastPage = Math.ceil(response.totalCount === 0 ? 0 : response.totalCount / limit);
            setPages(aLastPage);
            if (page > aLastPage && page !== 1) {
                if (request.offset === 0) {
                    setPage(1); // Ensure it's set to 1 when filtering/searching
                } else {
                    const newPage = aLastPage === 0 ? 1 : aLastPage;
                    if (page !== newPage) {
                        setPage(newPage);
                        const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
                        setOffset(newOffset);
                        request.offset = newOffset;

                        // Only re-fetch if we had to adjust the page
                        response = await getExpensesWithFilter(request);
                        aLastPage = Math.ceil(
                            response.totalCount === 0 ? 0 : response.totalCount / limit
                        );
                        setPages(aLastPage);
                        setTotalNumberExpense(response.totalCount);
                        setExpenses(response.data.slice(0, limit));
                    }
                }
            }
            setIsSorting(false);
        } catch (error) {
            const message = formatErrorMessage(error);
            const code = error.statusCode;
            if (code === 404) {
                setExpenses([]);
                setTotalNumberExpense(0);
                setPages(0);
            } else {
                setAlert({
                    message,
                    type: 'error',
                });
            }
        } finally {
            setIsLoading(false);

            reset({
                Statuses: request?.statuses ? request?.statuses : [],
                Divisions: request?.divisions,
                'Special Processing': request?.specialProcessing,
                Flags: request?.flags ? request.flags : [],
                'Expense Types': request?.expenseTypes,
                'Bill Types': request?.billTypes,
                Sources: request?.sources,
                'Needs Review Reasons': request?.needsReviewReasons,
                'Merchant States': request?.merchantStates,
                StartStartDate: request?.startStartDate,
                StartEndDate: request?.startEndDate,
                EndStartDate: request?.endStartDate,
                EndEndDate: request?.endEndDate,
                PostStartDate: request?.postStartDate,
                PostEndDate: request?.postEndDate,
                PeriodEndStart: request?.periodEndStart,
                PeriodEndEnd: request?.periodEndEnd,
                TransactionStartDate: request?.transactionStartDate,
                TransactionEndDate: request?.transactionEndDate,
                PayDateStart: request?.payDateStart,
                PayDateEnd: request?.payDateEnd,
                SubmittedStartDate: request?.submittedStartDate,
                SubmittedEndDate: request?.submittedEndDate,
            });
        }
    };

    const getData = async () => {
        setIsLoading(true);

        const ncreff = await checkFeatureFlag('NonClinicianReimbursementExpenseFeatures', false);
        setIsNCREFFEnabled(ncreff);

        const filterValues = getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement);
        const request = {
            expenseClass: ExpenseClassEnum.Reimbursement,
            offset,
            sortBy,
            sortDirection,
            limit,
            statuses: defaultFormValues.Statuses,
            divisions: defaultFormValues.Divisions,
            specialProcessing: defaultFormValues['Special Processing'],
            flags: defaultFormValues.Flags,
            transactionStartDate: defaultFormValues.TransactionStartDate,
            transactionEndDate: defaultFormValues.TransactionEndDate,
            expenseTypes: defaultFormValues['Expense Types'],
            sources: defaultFormValues.Sources,
            needsReviewReasons: defaultFormValues['Needs Review Reasons'],
            billTypes: defaultFormValues['Bill Types'],
            startStartDate: defaultFormValues.StartStartDate,
            startEndDate: defaultFormValues.StartEndDate,
            endStartDate: defaultFormValues.EndStartDate,
            endEndDate: defaultFormValues.EndEndDate,
            postStartDate: defaultFormValues.PostStartDate,
            postEndDate: defaultFormValues.PostEndDate,
            periodEndStart: defaultFormValues.PeriodEndStart,
            periodEndEnd: defaultFormValues.PeriodEndEnd,
            payDateStart: defaultFormValues.PayDateStart,
            payDateEnd: defaultFormValues.PayDateEnd,
            submittedStartDate: defaultFormValues.SubmittedStartDate,
            submittedEndDate: defaultFormValues.SubmittedEndDate,
            merchantStates: defaultFormValues['Merchant States'],
            search: filterValues ? filterValues.search : '',
            searchKey:
                filterValues && filterValues.searchKey
                    ? filterValues.searchKey
                    : config.searchKeys[0],
        };
        await Promise.all([
            getExpensesWithFilter(request),
            getExpenseCategories(ExpenseClassEnum.Reimbursement),
            getDivisions(false, true),
        ])
            .then(async ([response, cats, divs]) => {
                setTotalNumberExpense(response.totalCount);
                setExpenses(response.data.slice(0, limit));
                setDivisions(divs);
                setExpenseCategories(cats);
                let aLastPage = Math.ceil(
                    response.totalCount === 0 ? 0 : response.totalCount / limit
                );
                setPages(aLastPage);
                if (page > aLastPage && page !== 1) {
                    if (request.offset === 0) {
                        setPage(1); // Ensure it's set to 1 when filtering/searching
                    } else {
                        const newPage = aLastPage === 0 ? 1 : aLastPage;
                        if (page !== newPage) {
                            setPage(newPage);
                            const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
                            setOffset(newOffset);
                            request.offset = newOffset;

                            // Only re-fetch if we had to adjust the page
                            response = await getExpensesWithFilter(request);
                            aLastPage = Math.ceil(
                                response.totalCount === 0 ? 0 : response.totalCount / limit
                            );
                            setPages(aLastPage);
                            setTotalNumberExpense(response.totalCount);
                            setExpenses(response.data.slice(0, limit));
                        }
                    }
                }
                localStorage.setItem('expenseCriteria', JSON.stringify(request));
            })
            .catch((error) => {
                const message = formatErrorMessage(error);
                if (message === 'Internal Server Error') {
                    setExpenses([]);
                } else {
                    setAlert({
                        message,
                        type: 'error',
                    });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handlePageChange = async (e, newPage) => {
        setIsLoading(true);
        const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
        setOffset(newOffset);
        setPage(newPage);

        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement);

        currentCriteria.offset = newOffset;
        currentCriteria.page = newPage;
        setCriteria(currentCriteria);
        await fetchData(currentCriteria);
    };

    const handleUpdateExpenses = (updatedExpenses) => {
        setExpenses(updatedExpenses);
    };

    const refreshData = async () => {
        setIsLoading(true);
        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Reimbursement);
        await fetchData(currentCriteria);
    };

    const renderPagination = () => (
        <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            className={classes.paginationSpace}
        >
            <Grid item>
                <div className={classes.paginationNumber}>
                    {`Page ${page} of ${!expenses ? 0 : pages}`}
                </div>
            </Grid>
            <Grid item>
                {pages > 1 && (
                    <Pagination
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        page={page}
                        count={!expenses ? 0 : pages}
                        onChange={(e, page) => handlePageChange(e, page)}
                        renderItem={(item) => {
                            const hidden =
                                (item.type === 'previous' || item.type === 'next') && item.disabled;
                            if (hidden) return;
                            return (
                                <PaginationItem
                                    {...item}
                                    classes={{ outlined: classes.outlinedPageButton }}
                                />
                            );
                        }}
                    />
                )}
            </Grid>
        </Grid>
    );

    const renderFilters = () => (
        <FormProvider {...methods}>
            <ExpenseFiltersForm
                expenseClass={ExpenseClassEnum.Reimbursement}
                criteria={criteria}
                fetchData={fetchData}
                setIsLoading={setIsLoading}
                setOffset={setOffset}
                setPage={setPage}
                limit={limit}
                sortBy={sortBy}
                sortDirection={sortDirection}
                expenseCategories={expenseCategories}
                availableDivisions={divisions}
                pendingCCReview={false}
                isLoading={isLoading}
                isReimbursement
            />
        </FormProvider>
    );

    const renderExpenseTable = () => (
        <Paper classes={{ root: classes.PaperRoot }}>
            <ExpenseTable
                config={config}
                expenses={expenses}
                sortBy={sortBy}
                isLoading={isLoading}
                sortDirection={sortDirection}
                handleSort={handleSort}
                limit={limit}
                pendingCCReview={false}
                onUpdateExpenses={handleUpdateExpenses}
                onRefreshData={refreshData}
                isReimbursement
            />
        </Paper>
    );
    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <Grid>
                <Grid container justifyContent="space-between" spacing={2}>
                    <Grid item>
                        <Typography variant="h3" component="h3" className={classes.header}>
                            {enableNewCRListViewChanges
                                ? 'Clinician Reimbursement Expense Review'
                                : 'CC/CR Expense Review'}{' '}
                            ({totalNumberExpense || 0})
                        </Typography>
                    </Grid>
                    <Grid item sx={{ display: 'flex', gap: 2 }}>
                        {canDownload && (
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleDownload}
                                disabled={downloading}
                                sx={{
                                    minWidth: '180px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }} // Ensures consistent width
                            >
                                {downloading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Download Expenses'
                                )}
                            </Button>
                        )}

                        {!isViewOnly && (
                            <Button
                                aria-controls="search-key"
                                aria-haspopup="true"
                                onClick={() => history.push('/expenses')}
                                variant="contained"
                                color="primary"
                                id="search-key"
                            >
                                + Add Expense
                            </Button>
                        )}
                    </Grid>
                </Grid>

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit()}>
                        <Grid>{renderFilters()}</Grid>
                        {pages > 0 && renderPagination()}
                        <Grid>{renderExpenseTable()}</Grid>
                    </form>
                </FormProvider>

                {pages > 0 && renderPagination()}
            </Grid>
            <Alert alert={alert} setAlert={setAlert} />
        </Container>
    );
};

export default withStyles(styles)(ExpenseReview);
